import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Button from '../Button';
import config from '../../config';

const CreateSongModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: '',
    albumId: ''
  });
  const [albums, setAlbums] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [isLoadingAlbums, setIsLoadingAlbums] = useState(false);

  // Fetch albums for the dropdown
  useEffect(() => {
    if (isOpen) {
      fetchAlbums();
    }
  }, [isOpen]);

  const fetchAlbums = async () => {
    setIsLoadingAlbums(true);
    try {
      const response = await axios.get(`${config.API_URL}/albums`);
      setAlbums(response.data);
      
      // Set the first album as default if available
      if (response.data.length > 0 && !formData.albumId) {
        setFormData(prev => ({
          ...prev,
          albumId: response.data[0].id
        }));
      }
    } catch (err) {
      console.error('Error fetching albums:', err);
    } finally {
      setIsLoadingAlbums(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // Add username for our simplified MVP approach
      const songData = {
        ...formData,
        username: config.USERNAME
      };

      console.log('Submitting song data:', songData);
      
      // Call our serverless API to create the song
      const response = await axios.post(`${config.API_URL}/songs`, songData);
      console.log('Song created successfully:', response.data);
      
      // Close modal and refresh data
      onSuccess(response.data);
      onClose();
    } catch (err) {
      console.error('Error creating song:', err);
      console.error('Error details:', err.response?.data);
      setError(err.response?.data?.message || 'Failed to create song. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="mb-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Create New Song</h2>
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <p className="text-sm text-gray-500 mt-2">Basic information only - you can add details later</p>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4" role="alert">
            <p>{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="title">
              Title *
            </label>
            <input
              id="title"
              name="title"
              type="text"
              value={formData.title}
              onChange={handleChange}
              required
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            />
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="albumId">
              Album *
            </label>
            {isLoadingAlbums ? (
              <div className="flex items-center">
                <svg className="animate-spin h-5 w-5 mr-2 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Loading albums...
              </div>
            ) : albums.length > 0 ? (
              <select
                id="albumId"
                name="albumId"
                value={formData.albumId}
                onChange={handleChange}
                required
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              >
                <option value="">Select an album</option>
                {albums.map(album => (
                  <option key={album.id} value={album.id}>
                    {album.title} {album.artist ? `- ${album.artist}` : ''}
                  </option>
                ))}
              </select>
            ) : (
              <div className="text-red-500 text-sm">
                No albums available. Please create an album first.
              </div>
            )}
          </div>

          <div className="mb-6 mt-4">
            <p className="text-gray-600 text-sm italic">
              Additional details like lyrics, duration, and notes can be added after creating the song.
            </p>
          </div>

          <div className="flex justify-end space-x-3">
            <Button 
              variant="outline" 
              type="button" 
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button 
              variant="primary" 
              type="submit"
              disabled={isSubmitting || albums.length === 0}
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating...
                </>
              ) : 'Create Song'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateSongModal;
